/**
 * Streetlight bake (Task ST-2): instanced posts + heads for a settlement's
 * lamp records (ST-1's `buildStreetlights` output — STREETLIGHT_STRIDE floats
 * each: [cx,cy,cz, yaw, height, variant], planet-local base point).
 *
 * Two InstancedMeshes, always visible:
 *  - posts: a thin tall box from the lamp base up to `height` along the radial
 *    (outward) axis — shared dark-metal Lambert.
 *  - heads: a small emissive box at the post top — its material (`headMat`) is
 *    returned so the renderer's night pass can poke `emissive` warm (Task ST-3).
 *
 * Placement mirrors the building bake (buildingMeshes.ts): reconstruct a tangent
 * frame at the base point, compose a basis (up + any tangent) × scale, and set
 * the per-instance matrix. Unit geometries are module singletons (one BoxGeometry
 * for the post, one for the head) scaled per instance, so a whole settlement's
 * lamps cost two draw calls.
 */
import * as THREE from "three";
import { STREETLIGHT_STRIDE } from "@hyperlight/shared";

export interface StreetlightBake {
  /** Post boxes — one instance per lamp, base→height along the radial. */
  posts: THREE.InstancedMesh;
  /** Lamp-head boxes — emissive material (night glow), one per lamp. */
  heads: THREE.InstancedMesh;
  /** The head material — returned so the renderer pokes its emissive at night. */
  headMat: THREE.MeshLambertMaterial;
}

// ── ST-4: real PointLight pool sizing (consumed by the renderer) ──────────────
/**
 * How many REAL `THREE.PointLight`s the whole scene ever spends on streetlamps —
 * a small constant pool the renderer repositions onto the nearest lamps to the
 * player. This cap (not the number of lamps) is the performance contract: never
 * one light per lamp, never a per-region/per-frame allocation.
 */
export const STREETLIGHT_REAL_MAX = 12;
/** A pooled streetlamp light's falloff radius (metres) — its `PointLight.distance`.
 *  Range is ~free (the shader loop runs NUM_POINT_LIGHTS times regardless of
 *  whether a light reaches the fragment), so a wider radius just enlarges each
 *  lamp's lit footprint at no extra fragment cost. Count (STREETLIGHT_REAL_MAX)
 *  is the linear-cost knob — keep it CONSTANT so the point-light count never
 *  changes (see the pool note in renderer.ts). Non-shadow lights only. */
export const STREETLIGHT_LIGHT_RANGE_M = 40;

/**
 * Pure: pick the ≤ `n` nearest lamp indices (into the flat `lampXyz` xyz array,
 * 3 floats per lamp) to point `(px,py,pz)`, keeping only those within `range2`
 * (squared distance). Returns `[{ idx, d2 }]` ascending by `d2`, stable on ties
 * by `idx`. No THREE — same frame as `lampXyz` (planet-local); the renderer
 * transforms the player into that frame before calling.
 */
export function nearestLamps(
  lampXyz: Float32Array,
  px: number,
  py: number,
  pz: number,
  n: number,
  range2: number,
): { idx: number; d2: number }[] {
  const hits: { idx: number; d2: number }[] = [];
  const count = Math.floor(lampXyz.length / 3);
  for (let i = 0; i < count; i++) {
    const dx = lampXyz[i * 3] - px;
    const dy = lampXyz[i * 3 + 1] - py;
    const dz = lampXyz[i * 3 + 2] - pz;
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 <= range2) hits.push({ idx: i, d2 });
  }
  hits.sort((a, b) => a.d2 - b.d2 || a.idx - b.idx);
  return hits.length > n ? hits.slice(0, n) : hits;
}

// ── unit geometries (module singletons, scaled per instance) ──────────────────
// Post: a 1×1×1 box whose LOCAL origin sits at its BASE (translated +0.5 in y),
// so a per-instance y-scale of `height` grows it upward from the lamp base along
// the radial axis without shifting the foot underground. Head: a small box whose
// origin sits at ITS OWN centre, offset to the post top at compose time.
const POST_W = 0.16; // metres — thin metal pole
const HEAD_SIZE = 0.5; // metres — lamp-head box edge

let postGeom: THREE.BoxGeometry | null = null;
let headGeom: THREE.BoxGeometry | null = null;

function unitPost(): THREE.BoxGeometry {
  if (!postGeom) {
    postGeom = new THREE.BoxGeometry(1, 1, 1);
    postGeom.translate(0, 0.5, 0); // origin at base → +y scale grows upward
  }
  return postGeom;
}
function unitHead(): THREE.BoxGeometry {
  if (!headGeom) headGeom = new THREE.BoxGeometry(1, 1, 1);
  return headGeom;
}

/**
 * The post/head unit geometries — module singletons shared across every
 * settlement's lamps. The renderer's `disposeVis` traverse disposes per-settlement
 * geometry but MUST skip these (same guard the kit box/cone singletons use), or a
 * settlement teardown would free geometry other resident settlements still use.
 * Returns null for either if the bake has not run yet (nothing to guard).
 */
export function streetlightUnitGeoms(): {
  post: THREE.BoxGeometry | null;
  head: THREE.BoxGeometry | null;
} {
  return { post: postGeom, head: headGeom };
}

/**
 * A right-handed tangent frame at planet-local (px,py,pz): `up` is the outward
 * radial, `t1`/`t2` span the tangent plane. Same construction as renderer.ts /
 * buildingMeshes.ts (kept local so the bake is self-contained + node-testable).
 */
function tangentFrame(px: number, py: number, pz: number): { up: THREE.Vector3; t1: THREE.Vector3 } {
  const l = Math.hypot(px, py, pz) || 1;
  const up = new THREE.Vector3(px / l, py / l, pz / l);
  const rx = Math.abs(up.y) < 0.9 ? 0 : 1;
  const ry = Math.abs(up.y) < 0.9 ? 1 : 0;
  // t1 = normalize(ref × up)
  const t1 = new THREE.Vector3(
    ry * up.z - 0 * up.y,
    0 * up.x - rx * up.z,
    rx * up.y - ry * up.x,
  );
  if (t1.lengthSq() < 1e-12) t1.set(1, 0, 0);
  t1.normalize();
  return { up, t1 };
}

/**
 * Bake lamp records into two InstancedMeshes. Returns `null` for an empty array
 * (no lamps → no meshes to add).
 */
export function bakeStreetlights(lamps: Float64Array): StreetlightBake | null {
  const n = Math.floor(lamps.length / STREETLIGHT_STRIDE);
  if (n === 0) return null;

  const postMat = new THREE.MeshLambertMaterial({ color: 0x2b2b30 }); // dark metal
  const headMat = new THREE.MeshLambertMaterial({ color: 0xffd9a0, emissive: 0x000000 }); // warm, poked at night

  const posts = new THREE.InstancedMesh(unitPost(), postMat, n);
  const heads = new THREE.InstancedMesh(unitHead(), headMat, n);

  const base = new THREE.Vector3();
  const zA = new THREE.Vector3();
  const basis = new THREE.Matrix4();
  const quat = new THREE.Quaternion();
  const mtx = new THREE.Matrix4();
  const scale = new THREE.Vector3();
  const headPos = new THREE.Vector3();

  for (let i = 0; i < n; i++) {
    const o = i * STREETLIGHT_STRIDE;
    const cx = lamps[o], cy = lamps[o + 1], cz = lamps[o + 2];
    const height = lamps[o + 4];
    const { up, t1 } = tangentFrame(cx, cy, cz);
    // Basis: local +Y = radial up (post growth axis), +X = a tangent, +Z = X×Y.
    zA.crossVectors(t1, up).normalize();
    basis.makeBasis(t1, up, zA);
    quat.setFromRotationMatrix(basis);

    base.set(cx, cy, cz);
    // Post: base-origin unit box scaled to (POST_W, height, POST_W).
    mtx.compose(base, quat, scale.set(POST_W, height, POST_W));
    posts.setMatrixAt(i, mtx);

    // Head: a small box centred at the post top (base + up·height).
    headPos.copy(base).addScaledVector(up, height);
    mtx.compose(headPos, quat, scale.set(HEAD_SIZE, HEAD_SIZE, HEAD_SIZE));
    heads.setMatrixAt(i, mtx);
  }

  posts.instanceMatrix.needsUpdate = true;
  heads.instanceMatrix.needsUpdate = true;
  posts.computeBoundingSphere();
  heads.computeBoundingSphere();

  return { posts, heads, headMat };
}
